import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  OutlinedInput,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import {
  CloudDownload as CloudDownloadIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  GetApp as GetAppIcon,
  DateRange as DateRangeIcon,
  Business as BusinessIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
// Removed date picker imports to simplify dependencies
import {
  getSalesExportData,
  getSalesExportFilterOptions,
  exportSalesToGoogleSheets
} from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
}));

const StatsCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  color: 'white',
  '& .MuiTypography-h4': {
    fontWeight: 'bold',
    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function SalesExport() {
  // State management
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    itemTypes: [],
    shapes: [],
    sizes: [],
    brims: []
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    locationIds: [],
    itemTypes: [],
    shapes: [],
    sizes: [],
    brims: [],
    startDate: null,
    endDate: null,
    groupBy: 'item'
  });

  // UI states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportDialog, setExportDialog] = useState(false);
  const [customSheetName, setCustomSheetName] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // Load filter options on component mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    setLoadingFilters(true);
    try {
      console.log('Loading filter options...');
      const response = await getSalesExportFilterOptions();
      console.log('Filter options response:', response);
      
      if (response.success) {
        setFilterOptions(response.data);
        
        // Set default "All" selections for all filters
        setFilters(prev => ({
          ...prev,
          locationIds: response.data.locations?.map(loc => loc.db_location_id) || [],
          itemTypes: response.data.itemTypes?.map(type => type.type) || [],
          shapes: response.data.shapes?.map(shape => shape.shape) || [],
          sizes: response.data.sizes?.map(size => size.size) || [],
          brims: response.data.brims?.map(brim => brim.brim) || []
        }));
        
        showSnackbar(`Loaded ${response.data.locations?.length || 0} locations, ${response.data.itemTypes?.length || 0} types`, 'success');
      } else {
        showSnackbar('Failed to load filter options', 'error');
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
      showSnackbar('Error loading filter options: ' + error.message, 'error');
      
      // Show fallback message if API is not available
      if (error.message.includes('404') || error.message.includes('Network Error')) {
        showSnackbar('Sales export API not available. Please check deployment.', 'warning');
      }
    } finally {
      setLoadingFilters(false);
    }
  };

  const loadSalesData = async () => {
    setLoading(true);
    try {
      const response = await getSalesExportData(filters);
      if (response.success) {
        setSalesData(response.data);
        setPage(0); // Reset to first page when data changes
      } else {
        showSnackbar('Failed to load sales data', 'error');
      }
    } catch (error) {
      console.error('Error loading sales data:', error);
      showSnackbar('Error loading sales data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToSheets = async () => {
    if (salesData.length === 0) {
      showSnackbar('No data to export', 'warning');
      return;
    }

    setExporting(true);
    try {
      const response = await exportSalesToGoogleSheets(filters, customSheetName);
      if (response.success) {
        showSnackbar('Export completed successfully!', 'success');
        // Open the sheet in a new tab
        window.open(response.sheetUrl, '_blank');
        setExportDialog(false);
        setCustomSheetName('');
      } else {
        showSnackbar(response.message || 'Export failed', 'error');
      }
    } catch (error) {
      console.error('Error exporting to Google Sheets:', error);
      showSnackbar('Error exporting to Google Sheets', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      locationIds: [],
      itemTypes: [],
      shapes: [],
      sizes: [],
      brims: [],
      startDate: null,
      endDate: null,
      groupBy: 'item'
    });
    setSalesData([]);
  };

  const selectAllFilters = () => {
    setFilters(prev => ({
      ...prev,
      locationIds: filterOptions.locations?.map(loc => loc.db_location_id) || [],
      itemTypes: filterOptions.itemTypes?.map(type => type.type) || [],
      shapes: filterOptions.shapes?.map(shape => shape.shape) || [],
      sizes: filterOptions.sizes?.map(size => size.size) || [],
      brims: filterOptions.brims?.map(brim => brim.brim) || []
    }));
  };

  // Helper function to render chip labels with "All" when everything is selected
  const renderChipValue = (selected, options, labelKey, valueKey = null) => {
    if (loadingFilters) return 'Loading filters...';
    if (!options || options.length === 0) return 'No options available';
    if (selected.length === 0) return 'None selected';
    if (selected.length === options.length) return `All (${selected.length})`;
    if (selected.length > 3) return `${selected.length} selected`;
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.slice(0, 3).map((value) => {
          const option = valueKey 
            ? options.find(opt => opt[valueKey] === value)
            : options.find(opt => opt[labelKey] === value);
          const displayLabel = option ? option[labelKey] : value;
          return (
            <Chip key={value} label={displayLabel} size="small" />
          );
        })}
        {selected.length > 3 && <Chip label={`+${selected.length - 3} more`} size="small" />}
      </Box>
    );
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!salesData.length) return null;

    const totalRevenue = salesData.reduce((sum, row) => sum + (parseFloat(row.total_revenue) || 0), 0);
    const totalQuantity = salesData.reduce((sum, row) => sum + (parseFloat(row.total_quantity_sold) || 0), 0);
    const totalSales = salesData.reduce((sum, row) => sum + (parseFloat(row.total_sales_count) || 0), 0);

    return {
      totalRevenue,
      totalQuantity,
      totalSales,
      recordCount: salesData.length
    };
  }, [salesData]);

  // Table columns based on groupBy
  const getTableColumns = () => {
    switch (filters.groupBy) {
      case 'location':
        return [
          { id: 'location_name', label: 'Location Name', width: 200 },
          { id: 'total_sales_count', label: 'Sales Count', width: 120, align: 'right' },
          { id: 'total_quantity_sold', label: 'Quantity Sold', width: 120, align: 'right' },
          { id: 'total_revenue', label: 'Revenue ($)', width: 120, align: 'right', format: (value) => parseFloat(value || 0).toLocaleString('en-US', {style: 'currency', currency: 'USD'}) },
          { id: 'unique_items_sold', label: 'Unique Items', width: 120, align: 'right' },
          { id: 'first_sale_date', label: 'First Sale', width: 120, format: (value) => value ? new Date(value).toLocaleDateString() : '' },
          { id: 'last_sale_date', label: 'Last Sale', width: 120, format: (value) => value ? new Date(value).toLocaleDateString() : '' }
        ];
      case 'type':
        return [
          { id: 'type', label: 'Item Type', width: 150 },
          { id: 'total_sales_count', label: 'Sales Count', width: 120, align: 'right' },
          { id: 'total_quantity_sold', label: 'Quantity Sold', width: 120, align: 'right' },
          { id: 'total_revenue', label: 'Revenue ($)', width: 120, align: 'right', format: (value) => parseFloat(value || 0).toLocaleString('en-US', {style: 'currency', currency: 'USD'}) },
          { id: 'unique_items_sold', label: 'Unique Items', width: 120, align: 'right' },
          { id: 'locations_sold_in', label: 'Locations', width: 120, align: 'right' },
          { id: 'avg_unit_price', label: 'Avg Price ($)', width: 120, align: 'right', format: (value) => parseFloat(value || 0).toLocaleString('en-US', {style: 'currency', currency: 'USD'}) }
        ];
      case 'date':
        return [
          { id: 'sale_date', label: 'Sale Date', width: 150, format: (value) => value ? new Date(value).toLocaleDateString() : '' },
          { id: 'total_sales_count', label: 'Sales Count', width: 120, align: 'right' },
          { id: 'total_quantity_sold', label: 'Quantity Sold', width: 120, align: 'right' },
          { id: 'total_revenue', label: 'Revenue ($)', width: 120, align: 'right', format: (value) => parseFloat(value || 0).toLocaleString('en-US', {style: 'currency', currency: 'USD'}) },
          { id: 'unique_items_sold', label: 'Unique Items', width: 120, align: 'right' },
          { id: 'locations_with_sales', label: 'Locations', width: 120, align: 'right' }
        ];
      default: // 'item'
        return [
          { id: 'item_name', label: 'Item Name', width: 250 },
          { id: 'type', label: 'Type', width: 80 },
          { id: 'size', label: 'Size', width: 80 },
          { id: 'shape', label: 'Shape', width: 80 },
          { id: 'brim', label: 'Brim', width: 80 },
          { id: 'total_quantity_sold', label: 'Qty Sold', width: 100, align: 'right' },
          { id: 'total_revenue', label: 'Revenue ($)', width: 120, align: 'right', format: (value) => parseFloat(value || 0).toLocaleString('en-US', {style: 'currency', currency: 'USD'}) },
          { id: 'total_sales_count', label: 'Sales', width: 80, align: 'right' },
          { id: 'locations_sold_in', label: 'Locations', width: 100, align: 'right' }
        ];
    }
  };

  const tableColumns = getTableColumns();
  const paginatedData = salesData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Sales Data Export
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Filter and export sales data to Google Sheets with comprehensive analytics
        </Typography>

        {/* Filter Section */}
        <FilterSection elevation={2}>
          <Accordion expanded={filtersExpanded} onChange={() => setFiltersExpanded(!filtersExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon color="primary" />
                <Typography variant="h6">Filters & Options</Typography>
                <Badge 
                  badgeContent={
                    filters.locationIds.length + 
                    filters.itemTypes.length + 
                    filters.shapes.length + 
                    filters.sizes.length + 
                    filters.brims.length +
                    (filters.startDate ? 1 : 0) +
                    (filters.endDate ? 1 : 0)
                  } 
                  color="primary"
                  sx={{ ml: 1 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Group By */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Group By</InputLabel>
                    <Select
                      value={filters.groupBy}
                      label="Group By"
                      onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                    >
                      <MenuItem value="item">Item Details</MenuItem>
                      <MenuItem value="location">Location</MenuItem>
                      <MenuItem value="type">Item Type</MenuItem>
                      <MenuItem value="date">Date</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date Range */}
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={selectAllFilters}
                      size="small"
                      sx={{ flex: 1 }}
                      disabled={loadingFilters}
                    >
                      {loadingFilters ? 'Loading...' : 'Select All'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                      size="small"
                      sx={{ flex: 1 }}
                      disabled={loadingFilters}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Grid>

                {/* Locations Filter */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Locations</InputLabel>
                    <Select
                      multiple
                      value={filters.locationIds}
                      onChange={(e) => handleFilterChange('locationIds', e.target.value)}
                      input={<OutlinedInput label="Locations" />}
                      renderValue={(selected) => renderChipValue(selected, filterOptions.locations, 'name', 'db_location_id')}
                      MenuProps={MenuProps}
                      disabled={loadingFilters}
                    >
                      {filterOptions.locations?.map((location) => (
                        <MenuItem key={location.db_location_id} value={location.db_location_id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Item Types Filter */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Item Types</InputLabel>
                    <Select
                      multiple
                      value={filters.itemTypes}
                      onChange={(e) => handleFilterChange('itemTypes', e.target.value)}
                      input={<OutlinedInput label="Item Types" />}
                      renderValue={(selected) => renderChipValue(selected, filterOptions.itemTypes, 'type')}
                      MenuProps={MenuProps}
                      disabled={loadingFilters}
                    >
                      {filterOptions.itemTypes?.map((type) => (
                        <MenuItem key={type.type} value={type.type}>
                          {type.type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Shapes Filter */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Shapes</InputLabel>
                    <Select
                      multiple
                      value={filters.shapes}
                      onChange={(e) => handleFilterChange('shapes', e.target.value)}
                      input={<OutlinedInput label="Shapes" />}
                      renderValue={(selected) => renderChipValue(selected, filterOptions.shapes, 'shape')}
                      MenuProps={MenuProps}
                      disabled={loadingFilters}
                    >
                      {filterOptions.shapes?.map((shape) => (
                        <MenuItem key={shape.shape} value={shape.shape}>
                          {shape.shape}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Sizes Filter */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Sizes</InputLabel>
                    <Select
                      multiple
                      value={filters.sizes}
                      onChange={(e) => handleFilterChange('sizes', e.target.value)}
                      input={<OutlinedInput label="Sizes" />}
                      renderValue={(selected) => renderChipValue(selected, filterOptions.sizes, 'size')}
                      MenuProps={MenuProps}
                      disabled={loadingFilters}
                    >
                      {filterOptions.sizes?.map((size) => (
                        <MenuItem key={size.size} value={size.size}>
                          {size.size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Brims Filter */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Brims</InputLabel>
                    <Select
                      multiple
                      value={filters.brims}
                      onChange={(e) => handleFilterChange('brims', e.target.value)}
                      input={<OutlinedInput label="Brims" />}
                      renderValue={(selected) => renderChipValue(selected, filterOptions.brims, 'brim')}
                      MenuProps={MenuProps}
                      disabled={loadingFilters}
                    >
                      {filterOptions.brims?.map((brim) => (
                        <MenuItem key={brim.brim} value={brim.brim}>
                          {brim.brim}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={loadSalesData}
                  disabled={loading || loadingFilters}
                  startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  size="large"
                >
                  {loadingFilters ? 'Loading Filters...' : loading ? 'Loading...' : 'Load Data'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setExportDialog(true)}
                  disabled={salesData.length === 0 || loadingFilters}
                  startIcon={<CloudDownloadIcon />}
                  size="large"
                >
                  Export to Sheets
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </FilterSection>

        {/* Summary Statistics */}
        {summaryStats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <Typography variant="h4">{summaryStats.recordCount}</Typography>
                <Typography variant="body2">Records</Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <Typography variant="h4">
                  {summaryStats.totalRevenue.toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                </Typography>
                <Typography variant="body2">Total Revenue</Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <Typography variant="h4">{summaryStats.totalQuantity.toLocaleString()}</Typography>
                <Typography variant="body2">Total Quantity</Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <Typography variant="h4">{summaryStats.totalSales.toLocaleString()}</Typography>
                <Typography variant="body2">Total Sales</Typography>
              </StatsCard>
            </Grid>
          </Grid>
        )}

        {/* Data Table */}
        {salesData.length > 0 && (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {tableColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{ minWidth: column.width }}
                        sx={{ 
                          backgroundColor: 'primary.main', 
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row, index) => (
                    <TableRow hover key={index}>
                      {tableColumns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.format ? column.format(value) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={salesData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        )}

        {/* Empty State */}
        {!loading && salesData.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No sales data found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Apply filters and click "Load Data" to view sales information
            </Typography>
          </Paper>
        )}

        {/* Export Dialog */}
        <Dialog open={exportDialog} onClose={() => setExportDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Export to Google Sheets</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Export {salesData.length} records to a new Google Sheets document
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Sheet Name (Optional)"
              fullWidth
              variant="outlined"
              value={customSheetName}
              onChange={(e) => setCustomSheetName(e.target.value)}
              placeholder={`Sales Export - ${filters.groupBy} - ${new Date().toISOString().split('T')[0]}`}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleExportToSheets} 
              variant="contained"
              disabled={exporting}
              startIcon={exporting ? <CircularProgress size={20} /> : <GetAppIcon />}
            >
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
}

export default SalesExport; 